import React, { useState, useRef, useEffect } from 'react';
import { Upload, ImageIcon, Wand2, CheckCircle2, X, Download } from 'lucide-react';
import { fetchTransferStyles, generateStyleTransfer } from '../services/api';
import { refreshWallet } from '../hooks/useWallet';
import { useToast } from '../hooks/useToast';
import { useImageBlobPrefetch, fetchImageBlob } from '../hooks/useImageBlobPrefetch';
import { saveImage, canSaveToPhotos } from '../utils/saveImage';
import AlertDialog from '../components/AlertDialog';
import type { TransferStyle } from '../types';
import { RunningLoader } from '@/components/RunningLoader';

// Fallback gradients per style id (shown when no thumbnail image exists)
const FALLBACK_GRADIENTS: Record<string, string> = {
    anime: 'from-pink-400 via-orange-300 to-yellow-300',
    cyberpunk: 'from-purple-900 via-red-800 to-black',
    watercolor: 'from-pink-200 via-rose-300 to-purple-200',
    oilpainting: 'from-emerald-800 via-amber-700 to-red-900',
    '3d': 'from-blue-400 via-purple-500 to-indigo-600',
    sketch: 'from-slate-300 via-slate-400 to-slate-500',
    pixel: 'from-yellow-400 via-green-400 to-cyan-500',
    futuristic: 'from-cyan-900 via-blue-900 to-indigo-950',
};

const StyleTransfer: React.FC = () => {
    const [styles, setStyles] = useState<TransferStyle[]>([]);
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const uploadedFileRef = useRef<File | null>(null);
    const [selectedStyle, setSelectedStyle] = useState<TransferStyle | null>(null);
    const [generatedImage, setGeneratedImage] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [showExtraInput, setShowExtraInput] = useState(false);
    const [extraRequirements, setExtraRequirements] = useState('');
    const [pointsExhaustedMessage, setPointsExhaustedMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToast();
    // 結果顯示期間就把原圖備妥，讓儲存時能同步拿到 blob
    const takeGeneratedBlob = useImageBlobPrefetch(generatedImage ?? undefined);
    // 手機選了風格後自動下滑到上傳區
    const uploadSectionRef = useRef<HTMLDivElement>(null);
    const prevStyleIdRef = useRef<string | null>(null);

    useEffect(() => {
        fetchTransferStyles()
            .then(list => {
                // 保險再排一次：依 order 升冪，未設定者視為 0
                const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setStyles(sorted);
            })
            .catch(err => console.error('Failed to fetch transfer styles:', err));
    }, []);

    // 選了風格後自動捲動到上傳/生成區
    useEffect(() => {
        if (!selectedStyle) return;
        if (prevStyleIdRef.current === selectedStyle.id) return;
        prevStyleIdRef.current = selectedStyle.id;
        uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [selectedStyle]);

    // FloatingAssistant 事件監聽
    useEffect(() => {
        const handleSelectStyle = (e: Event) => {
            const { styleId } = (e as CustomEvent<{ styleId: string }>).detail;
            setStyles(prev => {
                const found = prev.find(s => s.id === styleId);
                if (found) setSelectedStyle(found);
                return prev;
            });
        };
        window.addEventListener('assistant:style-transfer:select-style', handleSelectStyle);
        return () => {
            window.removeEventListener('assistant:style-transfer:select-style', handleSelectStyle);
        };
    }, []);

    const handleFileSelect = (file: File) => {
        if (!file.type.match(/image\/(jpeg|png)/)) {
            alert('僅支援 JPG、PNG 格式');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            alert('圖片大小不可超過 10MB');
            return;
        }
        uploadedFileRef.current = file;
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                setUploadedImage(reader.result);
                setGeneratedImage(null);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
    };

    const handleGenerate = async () => {
        if (!uploadedFileRef.current || !selectedStyle) return;
        setIsGenerating(true);
        setGeneratedImage(null);

        try {
            const result = await generateStyleTransfer(uploadedFileRef.current, selectedStyle.id, extraRequirements);
            setGeneratedImage(result.imageUrl);
            window.dispatchEvent(new CustomEvent('jodoni:generation:complete'));
            refreshWallet();
        } catch (error: any) {
            if (error.isPointsExhausted) {
                console.warn('Style transfer blocked: points exhausted', error);
                setPointsExhaustedMessage(error.message || '本月點數已用完，請聯繫客服為您調整！');
            } else if (error.isCommunityViolation) {
                console.error('Style transfer blocked by community guidelines:', error);
                alert('生成失敗，違反社群生成規範。');
            } else {
                console.error('Style transfer failed:', error);
                alert('生成失敗，請稍後再試。');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    /** 儲存生成結果。iOS 走系統分享表單（可存進照片庫），其餘平台直接下載 */
    const handleSave = async () => {
        if (!generatedImage) return;
        try {
            // 預抓命中時是同步取得，中間沒有 await —— iOS 的 user activation 才不會過期
            const blob = takeGeneratedBlob() ?? await fetchImageBlob(generatedImage);
            await saveImage(blob, 'style-transfer.jpg', '風格轉換圖片');
        } catch (error) {
            console.error('圖片儲存失敗：', error);
            showToast('圖片儲存失敗，請稍後再試');
        }
    };

    const canGenerate = !!uploadedImage && !!selectedStyle;

    return (
        <div className="flex flex-col gap-6 md:gap-8">
            {/* Header — 手機隱藏（已由頂部列顯示） */}
            <div className="hidden lg:flex items-center gap-3 lg:order-1">
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                    <Wand2 className="text-white" size={22} />
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-white">人像風格生成 (Style Transfer)</h1>
            </div>

            {/* Top section: upload + confirm + result（手機堆疊、桌面三欄；皆排在風格列表下方） */}
            <div ref={uploadSectionRef} className="order-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 md:items-center scroll-mt-20">
                {/* Upload area */}
                <div className="space-y-2">
                    <p className="text-sm text-slate-400">原始圖片</p>
                    <div
                        className={`relative border-2 border-dashed rounded-2xl transition-all cursor-pointer aspect-square flex flex-col items-center justify-center overflow-hidden
                            ${isDragging ? 'border-indigo-400 bg-indigo-900/20' : 'border-slate-600 hover:border-slate-400 bg-slate-800/30'}
                        `}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={handleDrop}
                    >
                        {uploadedImage ? (
                            <>
                                <img src={uploadedImage} alt="uploaded" className="w-full h-full object-contain bg-slate-700" />
                                <button
                                    onClick={e => {
                                        e.stopPropagation();
                                        setUploadedImage(null);
                                        uploadedFileRef.current = null;
                                        setGeneratedImage(null);
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                    className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                                    title="移除圖片"
                                >
                                    <X size={14} />
                                </button>
                            </>
                        ) : (
                            <div className="flex flex-col items-center gap-3 text-slate-500 p-6 text-center">
                                <div className="w-12 h-12 rounded-full border-2 border-slate-600 flex items-center justify-center">
                                    <Upload size={22} />
                                </div>
                                <p className="text-sm font-medium text-slate-300">點擊或拖曳圖片上傳</p>
                                <p className="text-xs text-slate-500">支援 JPG, PNG (最大 10MB)</p>
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileSelect(file);
                            }}
                        />
                    </div>
                </div>

                {/* Center: confirm button + selected style preview */}
                <div className="flex flex-col items-center gap-3">
                    {/* Extra requirements toggle */}
                    <button
                        onClick={() => setShowExtraInput(v => !v)}
                        className="text-xs text-slate-400 hover:text-slate-200 border border-slate-600 hover:border-slate-400 rounded-full px-3 py-1 transition-colors"
                    >
                        {showExtraInput ? '✕ 收起額外要求' : '＋ 額外要求'}
                    </button>
                    {showExtraInput && (
                        <textarea
                            value={extraRequirements}
                            onChange={e => setExtraRequirements(e.target.value)}
                            placeholder="輸入額外要求，例如：請保留眼鏡"
                            rows={3}
                            className="w-48 text-xs text-slate-200 bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 resize-none focus:outline-none focus:border-indigo-500 placeholder-slate-500"
                        />
                    )}
                    <button
                        onClick={handleGenerate}
                        disabled={!canGenerate || isGenerating}
                        className={`relative px-8 py-3 rounded-full font-semibold text-white transition-all overflow-hidden
                            ${canGenerate && !isGenerating
                                ? 'jodoni-neon-btn cursor-pointer hover:scale-[1.03] active:scale-[0.98]'
                                : 'bg-slate-700 text-slate-400 cursor-not-allowed shadow-lg'
                            }`}
                    >
                        <span className="relative z-10">
                            {isGenerating ? '生成中...' : generatedImage ? '再次生成' : '確認生成'}
                        </span>
                    </button>

                    {selectedStyle && (
                        <div className="flex flex-col items-center gap-1">
                            <p className="text-xs text-slate-400">已選風格</p>
                            <div className="w-14 h-14 rounded-xl overflow-hidden shadow-md">
                                {selectedStyle.thumbnailUrl ? (
                                    <img src={selectedStyle.thumbnailProxyUrl || selectedStyle.thumbnailUrl} alt={selectedStyle.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className={`w-full h-full bg-gradient-to-br ${FALLBACK_GRADIENTS[selectedStyle.id] ?? 'from-slate-600 to-slate-800'}`} />
                                )}
                            </div>
                            <p className="text-xs text-indigo-400 font-medium">{selectedStyle.name}</p>
                        </div>
                    )}
                </div>

                {/* Result area */}
                <div className="space-y-2">
                    <p className="text-sm text-slate-400">生成結果</p>
                    <div className="aspect-square rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center overflow-hidden">
                        {isGenerating ? (
                            <div className="text-slate-500">
                                <RunningLoader size="lg" label="生成中" />
                            </div>
                        ) : generatedImage ? (
                            <img src={generatedImage} alt="generated" className="w-full h-full object-contain bg-slate-700" />
                        ) : (
                            <div className="flex flex-col items-center gap-3 text-slate-600">
                                <ImageIcon size={40} />
                                <p className="text-sm">等待生成中...</p>
                            </div>
                        )}
                    </div>
                    {generatedImage && (
                        <button
                            onClick={() => void handleSave()}
                            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
                        >
                            <Download size={15} />
                            {canSaveToPhotos() ? '儲存圖片' : '下載圖片'}
                        </button>
                    )}
                </div>
            </div>

            {/* Style list — 所有版本皆顯示在最上方 */}
            <div className="order-2 space-y-4">
                <div className="flex items-center gap-3">
                    <h2 className="text-base font-semibold text-slate-200">可選風格列表</h2>
                    <span className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded-full">
                        {styles.length} 種風格
                    </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {styles.map(style => (
                        <div key={style.id} className="flex flex-col gap-1.5">
                            <div
                                className={`group relative aspect-[4/3] w-full rounded-2xl transition-transform [perspective:1000px]
                                    ${selectedStyle?.id === style.id
                                        ? 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-slate-950'
                                        : 'hover:scale-[1.02]'
                                    }`}
                            >
                                <button
                                    onClick={() => setSelectedStyle(style)}
                                    className="relative w-full h-full [transform-style:preserve-3d] transition-transform duration-500 ease-out lg:group-hover:[transform:rotateY(180deg)]"
                                >
                                    {/* 卡片正面 — 縮圖 */}
                                    <div className="absolute inset-0 rounded-2xl overflow-hidden bg-slate-900 [backface-visibility:hidden] [-webkit-backface-visibility:hidden]">
                                        {style.thumbnailUrl ? (
                                            <img src={style.thumbnailProxyUrl || style.thumbnailUrl} alt={style.name} className="w-full h-full object-contain" />
                                        ) : (
                                            <div className={`w-full h-full bg-gradient-to-br ${FALLBACK_GRADIENTS[style.id] ?? 'from-slate-600 to-slate-800'}`} />
                                        )}
                                        {selectedStyle?.id === style.id && (
                                            <div className="absolute top-2 right-2 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                                                <CheckCircle2 size={14} className="text-white" />
                                            </div>
                                        )}
                                        
                                    </div>
                                    {/* 卡片背面 — 描述 */}
                                    <div className="absolute inset-0 rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-100 via-white to-slate-100 dark:from-indigo-900 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4 [transform:rotateY(180deg)] [backface-visibility:hidden] [-webkit-backface-visibility:hidden]">
                                        <p className="text-sm text-slate-800 dark:text-slate-100 text-center leading-relaxed">
                                            {style.description || style.name}
                                        </p>
                                    </div>
                                </button>
                            </div>
                            <p className={`text-sm font-medium text-center ${selectedStyle?.id === style.id ? 'text-indigo-400' : 'text-slate-300'}`}>
                                {style.name}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* 點數不足對話框 — 來自 server 402 錯誤 */}
            {pointsExhaustedMessage && (
                <AlertDialog
                    title="點數不足"
                    message={pointsExhaustedMessage}
                    severity="warning"
                    onClose={() => setPointsExhaustedMessage(null)}
                />
            )}
        </div>
    );
};

export default StyleTransfer;
